import { ESymbolCategory } from "./builder.dtos";
import { SpecialDIIds } from "./special-di-ids";
import { CHART_DATA_ROLE_TO_SYMBOL_KEY, isBasicChartTag } from "./widget-options-catalog";

/** The `IDisplayItem` fields that carry the screen-contract channels. */
export type ContractChannelField = "contractId" | "dataRef" | "fieldRef" | "interactionRef";

export interface IEditableSourceBindingExpression {
  type: "current" | "screenInput" | "default" | "componentValue" | "actionResult" | "actionField";
  fieldName?: string;
  inputName?: string;
  defaultName?: string;
  sourceKuikaId?: string;
  sourcePropertyName?: string;
  actionId?: string;
}

export interface IEditableSourceCodecOptions {
  simplifyStyleObj?: (styleObj: any) => IEditableSourceSimplifiedStyle | null;
}

export interface IEditableSourceSerializeInput {
  screenDesign: any;
  screenDictionary?: Record<number, any>;
  componentDefinitions?: any[];
}

export interface IEditableSourceSimplifiedStyle {
  text?: Record<string, unknown>;
  fill?: {
    backgroundGradientCss?: unknown;
    backgroundColor?: unknown;
    backgroundImage?: unknown;
    [key: string]: unknown;
  };
  position?: Record<string, unknown>;
  border?: Record<string, unknown>;
  shadow?: {
    x?: unknown;
    y?: unknown;
    blur?: unknown;
    spread?: unknown;
    color?: unknown;
    [key: string]: unknown;
  };
}

interface IEditableSerializerContext {
  componentDefinitions: Map<string, Map<string, any>>;
  pageInitActionDefinitionIds: Set<string>;
  pageInitActionIds: Set<string>;
  pageInitActionNames: Set<string>;
}

interface IEditableDatasourceContext {
  actionName: string;
  actionDefinitionId?: string;
  resultActionId?: string;
}

const PIXEL_STYLE_KEYS = new Set([
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "font-size",
  "letter-spacing",
  "line-height",
  "border-radius",
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-right-radius",
  "border-bottom-left-radius",
  "border-width",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "shadow-x",
  "shadow-y",
  "shadow-blur",
  "shadow-spread"
]);

const DEFAULT_BINDING_LABELS: Record<string, string> = {
  DateNow: "Now",
  NewGuid: "New Guid",
  EmptyGuid: "Empty Guid",
  Null: "null",
  GetMyUsername: "Current Username",
  CurrentUserid: "Current Userid",
  GetVersionInfo: "Version Info",
  ClientIpAddress: "Client IP Address"
};

const BINDING_REGEX = /^\{(.+)\}$/;

export class EditableSourceCodec {
  static readonly SPECIAL_SECTION_TAGS: Array<[number, string]> = [
    [SpecialDIIds.headerDI, "header"],
    [SpecialDIIds.pageHeaderDI, "pageHeader"],
    [SpecialDIIds.pageFooterDI, "pageFooter"],
    [SpecialDIIds.footerDI, "footer"],
    [SpecialDIIds.layoutHeaderDI, "layoutHeader"],
    [SpecialDIIds.layoutLeftDI, "layoutLeft"],
    [SpecialDIIds.layoutRightDI, "layoutRight"],
    [SpecialDIIds.layoutFooterDI, "layoutFooter"]
  ];

  static readonly SPECIAL_TAG_IDS: Record<string, string> = Object.freeze({
    body: String(SpecialDIIds.rootDI),
    ...Object.fromEntries(
      EditableSourceCodec.SPECIAL_SECTION_TAGS.map(([specialId, tagName]) => [tagName.toLowerCase(), String(specialId)])
    )
  });

  private static readonly CANONICAL_SPECIAL_TAGS_BY_ID = new Map<string, string>([
    [String(SpecialDIIds.rootDI), "body"],
    ...EditableSourceCodec.SPECIAL_SECTION_TAGS.map(([specialId, tagName]) => [String(specialId), tagName] as const)
  ]);

  private static readonly SPECIAL_SECTION_ID_SET = new Set(
    EditableSourceCodec.SPECIAL_SECTION_TAGS.map(([specialId]) => specialId)
  );

  /**
   * Markup attribute ↔ `IDisplayItem` field for the screen-contract channels.
   * One table so the serializer and the markup translator cannot drift apart in
   * the one way that matters: a channel written by one side and not read by the
   * other is indistinguishable from an unset channel.
   *
   * `contractId` leads because it is the component's identity in our namespace
   * and the references are read relative to it. `friendlyName` is deliberately
   * NOT here — the system owns that field and this serializer has never emitted
   * it.
   */
  static readonly CONTRACT_ATTRS: ReadonlyArray<[string, ContractChannelField]> = Object.freeze([
    ["contractId", "contractId"],
    ["dataRef", "dataRef"],
    ["fieldRef", "fieldRef"],
    ["interactionRef", "interactionRef"]
  ] as ReadonlyArray<[string, ContractChannelField]>);

  constructor(private readonly options: IEditableSourceCodecOptions = {}) {}

  static getSpecialTagNames(): string[] {
    return Object.keys(EditableSourceCodec.SPECIAL_TAG_IDS);
  }

  static getSpecialTagId(tagName: string | undefined | null): string | undefined {
    if (!tagName) return undefined;
    return EditableSourceCodec.SPECIAL_TAG_IDS[String(tagName).toLowerCase()];
  }

  static getCanonicalSpecialTagName(kuikaId: string | number | undefined | null): string | undefined {
    if (kuikaId == null) return undefined;
    return EditableSourceCodec.CANONICAL_SPECIAL_TAGS_BY_ID.get(String(kuikaId));
  }

  static parseBindingExpression(value: string): IEditableSourceBindingExpression | null {
    if (!value) return null;

    const match = value.match(BINDING_REGEX);
    if (!match) return null;

    const inner = match[1].trim();
    if (!inner) return null;

    const lowerInner = inner.toLowerCase();

    if (lowerInner.startsWith("current.")) {
      const fieldName = inner.slice("current.".length).trim();
      if (!fieldName) return null;
      return { type: "current", fieldName };
    }

    if (lowerInner.startsWith("screeninput:")) {
      const colonIdx = inner.indexOf(":");
      const inputName = inner.slice(colonIdx + 1).trim();
      if (!inputName) return null;
      return { type: "screenInput", inputName };
    }

    if (lowerInner.startsWith("default:")) {
      const colonIdx = inner.indexOf(":");
      const defaultName = inner.slice(colonIdx + 1).trim();
      if (!defaultName) return null;
      return { type: "default", defaultName };
    }

    if (lowerInner.startsWith("componentvalue:")) {
      const colonIdx = inner.indexOf(":");
      const bindingPath = inner.slice(colonIdx + 1).trim();
      const dotIdx = bindingPath.indexOf(".");
      if (dotIdx <= 0 || dotIdx === bindingPath.length - 1) return null;
      const sourceKuikaId = bindingPath.slice(0, dotIdx).trim();
      const sourcePropertyName = bindingPath.slice(dotIdx + 1).trim();
      if (!sourceKuikaId || !sourcePropertyName) return null;
      return { type: "componentValue", sourceKuikaId, sourcePropertyName };
    }

    if (lowerInner.startsWith("actionresult:")) {
      const colonIdx = inner.indexOf(":");
      const actionId = inner.slice(colonIdx + 1).trim();
      if (!actionId) return null;
      return { type: "actionResult", actionId };
    }

    if (lowerInner.startsWith("actionfield:")) {
      const colonIdx = inner.indexOf(":");
      const bindingPath = inner.slice(colonIdx + 1).trim();
      const dotIdx = bindingPath.indexOf(".");
      if (dotIdx <= 0 || dotIdx === bindingPath.length - 1) return null;
      const actionId = bindingPath.slice(0, dotIdx).trim();
      const fieldName = bindingPath.slice(dotIdx + 1).trim();
      if (!actionId || !fieldName) return null;
      return { type: "actionField", actionId, fieldName };
    }

    return null;
  }

  parseBindingExpression(value: string): IEditableSourceBindingExpression | null {
    return EditableSourceCodec.parseBindingExpression(value);
  }

  serializeScreen(input: IEditableSourceSerializeInput): string {
    const { screenDesign, screenDictionary, componentDefinitions } = input;
    const rootDi = screenDictionary?.[SpecialDIIds.rootDI];
    const context: IEditableSerializerContext = {
      componentDefinitions: this.buildComponentDefinitionMap(componentDefinitions),
      ...this.buildPageInitLookup(rootDi)
    };

    const attrs: Array<[string, string]> = [["kuikaId", String(SpecialDIIds.rootDI)]];
    const rootStyle = this.serializeInlineStyle(rootDi?.styleObj);
    if (rootStyle) {
      attrs.push(["style", rootStyle]);
    }

    const childLines: string[] = [];
    for (const [specialId, tagName] of EditableSourceCodec.SPECIAL_SECTION_TAGS) {
      if (specialId === SpecialDIIds.pageFooterDI || specialId === SpecialDIIds.footerDI) break;
      const di = screenDictionary?.[specialId];
      if (di) {
        childLines.push(this.serializeSpecialSection(di, tagName, context, "  "));
      }
    }

    const regularRootChildren = this.getRegularRootChildren(screenDesign, screenDictionary);
    childLines.push(...regularRootChildren.map((di) => this.serializeEditableNode(di, context, "  ")));

    for (const [specialId, tagName] of EditableSourceCodec.SPECIAL_SECTION_TAGS) {
      if (specialId === SpecialDIIds.headerDI || specialId === SpecialDIIds.pageHeaderDI) continue;
      const di = screenDictionary?.[specialId];
      if (di) {
        childLines.push(this.serializeSpecialSection(di, tagName, context, "  "));
      }
    }

    const attrString = " " + attrs.map(([name, value]) => `${name}="${this.escapeAttr(value)}"`).join(" ");
    if (childLines.length === 0) {
      return `<body${attrString}/>`;
    }

    return `<body${attrString}>\n${childLines.join("\n")}\n</body>`;
  }

  private camelToKebab(name: string): string {
    return name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  }

  private escapeAttr(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  private formatStyleValue(name: string, value: unknown): string | null {
    if (value == null || value === "") return null;
    if (typeof value === "number" && PIXEL_STYLE_KEYS.has(name)) {
      return `${value}px`;
    }
    return String(value);
  }

  private pushStyleDeclaration(declarations: string[], name: string, value: unknown): void {
    const formatted = this.formatStyleValue(name, value);
    if (formatted == null) return;
    declarations.push(`${name}:${formatted}`);
  }

  private serializeStyleEntries(
    declarations: string[],
    section: Record<string, unknown>,
    options?: { aliases?: Record<string, string> }
  ): void {
    for (const [key, value] of Object.entries(section)) {
      const name = options?.aliases?.[key] || this.camelToKebab(key);
      this.pushStyleDeclaration(declarations, name, value);
    }
  }

  private serializeInlineStyle(styleObj: any): string | null {
    const simplified = this.options.simplifyStyleObj?.(styleObj) || null;
    if (!simplified) return null;

    const declarations: string[] = [];

    if (simplified.text) {
      this.serializeStyleEntries(declarations, simplified.text);
    }

    if (simplified.fill) {
      if (simplified.fill.backgroundGradientCss) {
        this.pushStyleDeclaration(declarations, "background-image", simplified.fill.backgroundGradientCss);
      } else {
        this.pushStyleDeclaration(declarations, "background-color", simplified.fill.backgroundColor);
      }
      this.pushStyleDeclaration(declarations, "background-image", simplified.fill.backgroundImage);
    }

    if (simplified.position) {
      this.serializeStyleEntries(declarations, simplified.position);
    }

    if (simplified.border) {
      this.serializeStyleEntries(declarations, simplified.border);
    }

    if (simplified.shadow) {
      const shadowParts = [
        simplified.shadow.x ?? 0,
        simplified.shadow.y ?? 0,
        simplified.shadow.blur ?? 0,
        simplified.shadow.spread ?? 0
      ].map((part) => `${part}px`);
      if (simplified.shadow.color) {
        shadowParts.push(String(simplified.shadow.color));
      }
      declarations.push(`box-shadow:${shadowParts.join(" ")}`);
    }

    return declarations.length > 0 ? declarations.join("; ") : null;
  }

  private serializePropertyValue(value: unknown): string | null {
    if (value == null || value === "") return null;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }

  private buildComponentDefinitionMap(componentDefinitions?: any[]): Map<string, Map<string, any>> {
    const definitions = new Map<string, Map<string, any>>();
    if (!Array.isArray(componentDefinitions)) return definitions;

    for (const component of componentDefinitions) {
      const propertyMap = new Map<string, any>();
      for (const property of component?.properties || []) {
        if (property?.name) propertyMap.set(property.name, property);
      }
      if (component?.id) {
        definitions.set(component.id, propertyMap);
      }
    }

    return definitions;
  }

  private extractDataFieldName(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const match = value.match(/^\[datafield:([^\]]+)\]$/i);
    return match?.[1]?.trim() || null;
  }

  private normalizeDefaultBindingName(symbol: any): string | null {
    const rawName = symbol?.label || symbol?.defaultValueName;
    if (!rawName) return null;
    return DEFAULT_BINDING_LABELS[String(rawName)] || String(rawName);
  }

  private buildPageInitLookup(
    rootDi: any
  ): Pick<IEditableSerializerContext, "pageInitActionDefinitionIds" | "pageInitActionIds" | "pageInitActionNames"> {
    const pageInitActionDefinitionIds = new Set<string>();
    const pageInitActionIds = new Set<string>();
    const pageInitActionNames = new Set<string>();

    const pageInitEvent = rootDi?.item?.events?.find((event: any) => event?.eventName === "pageInit");
    for (const action of pageInitEvent?.actions || []) {
      if (action?.actionDefinitionId) pageInitActionDefinitionIds.add(String(action.actionDefinitionId));
      if (action?.actionId) pageInitActionIds.add(String(action.actionId));
      if (action?.id) pageInitActionIds.add(String(action.id));
      if (action?.name) pageInitActionNames.add(String(action.name));
      if (action?.label) pageInitActionNames.add(String(action.label));
    }

    return { pageInitActionDefinitionIds, pageInitActionIds, pageInitActionNames };
  }

  private getNodeDatasourceContext(
    di: any,
    context: IEditableSerializerContext
  ):
    | (IEditableDatasourceContext & {
        propertyName: string;
        dataTextField?: string;
        dataValueField?: string;
        chartRoles?: [string, string][];
        wirePageInit: boolean;
      })
    | null {
    const tagName: string = String(di?.item?.name ?? "");
    for (const prop of di?.item?.properties || []) {
      if (prop?.name !== "dataSource" && prop?.name !== "options") continue;
      const symbol = prop?.symbol;
      const actionName = symbol?.actionDefinitionObjectPath;
      if (!actionName) continue;

      const actionDefinitionId = symbol?.actionDefinitionId ? String(symbol.actionDefinitionId) : undefined;
      const resultActionId = symbol?.resultActionId ? String(symbol.resultActionId) : undefined;
      const normalizedActionName = String(actionName);
      const wirePageInit =
        (actionDefinitionId && context.pageInitActionDefinitionIds.has(actionDefinitionId)) ||
        (resultActionId && context.pageInitActionIds.has(resultActionId)) ||
        context.pageInitActionNames.has(normalizedActionName);

      return {
        propertyName: prop.name,
        actionName: normalizedActionName,
        actionDefinitionId,
        resultActionId,
        dataTextField: symbol?.fieldToDisplay ? String(symbol.fieldToDisplay) : undefined,
        dataValueField: symbol?.fieldToUseAsKey ? String(symbol.fieldToUseAsKey) : undefined,
        chartRoles: this.extractChartRoles(tagName, symbol),
        wirePageInit
      };
    }

    return null;
  }

  /**
   * For a classic flat-key chart (Line/Bar/Pie/Donut/Area/Radar/Gauge),
   * project the configured `ISymbol` chart fields back to their markup
   * role attributes (`chartDataDisplayLabel` → `dataLabel`, …) so the
   * editable source round-trips. Returns `undefined` for non-chart tags
   * or when no chart role is configured.
   */
  private extractChartRoles(tagName: string, symbol: any): [string, string][] | undefined {
    if (!isBasicChartTag(tagName) || !symbol) return undefined;
    const roles: [string, string][] = [];
    for (const [role, symbolKey] of Object.entries(CHART_DATA_ROLE_TO_SYMBOL_KEY)) {
      const value: unknown = symbol[symbolKey];
      if (value !== undefined && value !== null && value !== "") {
        roles.push([role, String(value)]);
      }
    }
    return roles.length > 0 ? roles : undefined;
  }

  private serializeBindingExpression(symbol: any, activeDatasource?: IEditableDatasourceContext | null): string | null {
    if (!symbol) return null;

    switch (symbol.category) {
      case ESymbolCategory.default: {
        const defaultName = this.normalizeDefaultBindingName(symbol);
        return defaultName ? `{default:${defaultName}}` : null;
      }
      case ESymbolCategory.screenInput:
        return symbol.inputName ? `{screenInput:${symbol.inputName}}` : null;
      case ESymbolCategory.displayItemProp:
        return symbol.diId != null && symbol.diPropName ? `{componentValue:${symbol.diId}.${symbol.diPropName}}` : null;
      case ESymbolCategory.actionResult:
        return symbol.resultActionId ? `{actionResult:${symbol.resultActionId}}` : null;
      case ESymbolCategory.current:
      case ESymbolCategory.actionField: {
        const fieldName = symbol.fieldName || this.extractDataFieldName(symbol.fixedValue);
        if (!fieldName) return null;
        const actionId = symbol.parentActionId || symbol.resultActionId;

        const matchesActiveDatasource =
          activeDatasource &&
          (!actionId ||
            actionId === activeDatasource.resultActionId ||
            symbol.actionDefinitionId === activeDatasource.actionDefinitionId);

        if (matchesActiveDatasource) {
          return `{current.${String(fieldName).replace(/^current\./i, "")}}`;
        }

        return actionId ? `{actionField:${actionId}.${fieldName}}` : null;
      }
      default:
        return null;
    }
  }

  private resolveEditablePropertyValue(prop: any, activeDatasource?: IEditableDatasourceContext | null): string | null {
    const bindingValue = this.serializeBindingExpression(prop?.symbol, activeDatasource);
    if (bindingValue) return bindingValue;

    const rawValue =
      prop?.symbol?.fixedValue !== undefined && prop?.symbol?.fixedValue !== ""
        ? prop.symbol.fixedValue
        : prop?.value !== undefined && prop?.value !== ""
          ? prop.value
          : undefined;

    return this.serializePropertyValue(rawValue);
  }

  private serializeEditableNode(
    di: any,
    context: IEditableSerializerContext,
    indent: string,
    activeDatasource?: IEditableDatasourceContext | null
  ): string {
    const attrs: Array<[string, string]> = [];
    if (di.id != null) {
      attrs.push(["kuikaId", String(di.id)]);
    }
    // Screen-contract channels, emitted BEFORE properties so a reader sees what a
    // component IS before what it looks like.
    //
    // They have to survive this serializer specifically: `editableSource` is the
    // only markup form of the LIVE canvas, and `ui_edit_source` re-renders from
    // whatever it contains. An attribute the serializer omits is an attribute the
    // next full-body edit DELETES — silently, because the tree still validates.
    // Omitting them here would make every source edit a de-wiring event.
    //
    // This is also why `contractId` is a field of our own rather than a borrowed
    // `id=`/`friendlyName`: our identity is emitted here, so a later turn can read
    // it back, verify it, and repair it. The system's `friendlyName` is not
    // serialized and we no longer need it to be.
    for (const [name, field] of EditableSourceCodec.CONTRACT_ATTRS) {
      const channel: unknown = di?.[field];
      if (typeof channel === "string" && channel.length > 0) attrs.push([name, channel]);
    }

    const propDefinitions = context.componentDefinitions.get(di.item?.id) || new Map<string, any>();
    const datasourceContext = this.getNodeDatasourceContext(di, context);
    for (const prop of di.item?.properties || []) {
      if (!prop?.name || prop.selectedType === 3 || prop.isDeprecated === true) continue;
      const def = propDefinitions.get(prop.name);
      if (def?.isTechnical === true || def?.isDeprecated === true) continue;

      if (datasourceContext && prop.name === datasourceContext.propertyName) {
        attrs.push(["datasource", datasourceContext.actionName]);
        if (datasourceContext.dataTextField) attrs.push(["dataTextField", datasourceContext.dataTextField]);
        if (datasourceContext.dataValueField) attrs.push(["dataValueField", datasourceContext.dataValueField]);
        for (const [role, value] of datasourceContext.chartRoles ?? []) {
          attrs.push([role, value]);
        }
        if (datasourceContext.wirePageInit) attrs.push(["pageInit", "true"]);
        continue;
      }

      const serializedValue = this.resolveEditablePropertyValue(prop, activeDatasource);
      if (serializedValue == null) continue;
      attrs.push([prop.name, serializedValue]);
    }

    const inlineStyle = this.serializeInlineStyle(di.styleObj);
    if (inlineStyle) {
      attrs.push(["style", inlineStyle]);
    }

    const attrString =
      attrs.length > 0 ? " " + attrs.map(([name, value]) => `${name}="${this.escapeAttr(value)}"`).join(" ") : "";

    const children = Array.isArray(di.children) ? di.children : [];
    if (children.length === 0) {
      return `${indent}<${di.item?.name || "Unknown"}${attrString}/>`;
    }

    const childIndent = indent + "  ";
    const childLines = children.map((child: any) =>
      this.serializeEditableNode(child, context, childIndent, datasourceContext || activeDatasource)
    );
    return `${indent}<${di.item?.name || "Unknown"}${attrString}>\n${childLines.join("\n")}\n${indent}</${
      di.item?.name || "Unknown"
    }>`;
  }

  private serializeSpecialSection(
    di: any,
    tagName: string,
    context: IEditableSerializerContext,
    indent: string
  ): string {
    const attrs: Array<[string, string]> = [["kuikaId", String(di.id)]];
    const inlineStyle = this.serializeInlineStyle(di?.styleObj);
    if (inlineStyle) {
      attrs.push(["style", inlineStyle]);
    }

    const attrString = " " + attrs.map(([name, value]) => `${name}="${this.escapeAttr(value)}"`).join(" ");
    const children = Array.isArray(di?.children) ? di.children : [];
    if (children.length === 0) {
      return `${indent}<${tagName}${attrString}/>`;
    }

    const childIndent = indent + "  ";
    const childLines = children.map((child: any) => this.serializeEditableNode(child, context, childIndent));
    return `${indent}<${tagName}${attrString}>\n${childLines.join("\n")}\n${indent}</${tagName}>`;
  }

  private getRegularRootChildren(screenDesign: any, screenDictionary?: Record<number, any>): any[] {
    const rootDi = screenDictionary?.[SpecialDIIds.rootDI];
    const rootChildren = Array.isArray(rootDi?.children) ? rootDi.children : undefined;
    if (rootChildren && rootChildren.length > 0) {
      return rootChildren.filter((child: any) => child && !EditableSourceCodec.SPECIAL_SECTION_ID_SET.has(child.id));
    }

    return Array.isArray(screenDesign?.displayItems) ? screenDesign.displayItems : [];
  }
}
